/**
 * InAppNotificationManager — Iter. 406.fr.53
 * ─────────────────────────────────────────────
 * Global "duo d'engagement" controller mounted once in Layout.js.
 *
 *  • Pop-up Dopamine "6/6 Perfect": full-screen festive modal (gold/fuchsia)
 *    triggered when the last pillar dot flips to hit — validates the brain
 *    with an immediate "1% Extra Miles" reward.
 *
 *  • Bannière Urgence Tactique: elegant top banner shown when the user
 *    opens the app after 18h local with score < 65 and at least one dot
 *    still in the red — pushes them to secure their score before midnight.
 *
 * State sources:
 *  - Polls `/api/discipline/today` every 45 s (aligned with DisciplineScore).
 *  - Listens for `yumix:perfect-day` events dispatched by PillarDots so
 *    a flip detected anywhere in the UI (Morning Debrief, Dashboard) also
 *    triggers the modal.
 *
 * Per-day dedup via localStorage so the modal fires at most once/day.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { X, Trophy, Sparkles } from 'lucide-react';
import { useAuth, useLanguage, api } from '../context/AppContext';
import { getLocalDateString } from '../utils/dateUtils';

const PERFECT_DISMISS_KEY = 'yumix_perfect_modal_shown';
const POLL_INTERVAL_MS = 45_000;

const perfectShownToday = (userId) => {
  try {
    return localStorage.getItem(`${PERFECT_DISMISS_KEY}_${userId}_${getLocalDateString()}`) === '1';
  } catch { return false; }
};
const markPerfectShown = (userId) => {
  try { localStorage.setItem(`${PERFECT_DISMISS_KEY}_${userId}_${getLocalDateString()}`, '1'); }
  catch { /* */ }
};

const InAppNotificationManager = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const fr = language === 'fr';

  const [disciplineData, setDisciplineData] = useState(null);
  const [showPerfectModal, setShowPerfectModal] = useState(false);
  const lastHitCountRef = useRef(0);
  const pollTimerRef = useRef(null);

  const userId = user?.user_id;

  // Compute pillar entries respecting the "hide fasting when no protocol" rule.
  const evaluate = useCallback((data) => {
    if (!data || !data.pillars) return null;
    const entries = Object.entries(data.pillars)
      .filter(([key, p]) => !(key === 'fasting' && p && p.has_protocol === false));
    const hitCount = entries.filter(([, p]) => p.hit).length;
    const total = entries.length;
    const missed = entries
      .filter(([, p]) => !p.hit)
      .sort((a, b) => (b[1].max || 0) - (a[1].max || 0));
    return {
      score: data.score || 0,
      hitCount,
      total,
      perfect: total > 0 && hitCount === total,
      missed,
    };
  }, []);

  // Fire the modal — respects per-day dedup + user opt-out signals.
  const fireModalIfPerfect = useCallback((snapshot) => {
    if (!userId || !snapshot?.perfect) return;
    if (perfectShownToday(userId)) return;
    markPerfectShown(userId);
    setShowPerfectModal(true);
  }, [userId]);

  // Fetch loop.
  useEffect(() => {
    if (!userId) return undefined;
    let alive = true;
    const fetchOnce = () => api.get('/discipline/today')
      .then((res) => {
        if (!alive) return;
        setDisciplineData(res.data);
        const snap = evaluate(res.data);
        if (!snap) return;
        // Transition detection: last hit-count < total, new hit-count == total.
        if (snap.perfect && lastHitCountRef.current < snap.total) {
          fireModalIfPerfect(snap);
        }
        lastHitCountRef.current = snap.hitCount;
      })
      .catch(() => {});
    fetchOnce();
    pollTimerRef.current = setInterval(fetchOnce, POLL_INTERVAL_MS);
    return () => {
      alive = false;
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [userId, evaluate, fireModalIfPerfect]);

  // Listen for cross-component perfect-day pings (from PillarDots).
  useEffect(() => {
    const onPerfect = () => {
      if (!userId || perfectShownToday(userId)) return;
      markPerfectShown(userId);
      setShowPerfectModal(true);
    };
    window.addEventListener('yumix:perfect-day', onPerfect);
    return () => window.removeEventListener('yumix:perfect-day', onPerfect);
  }, [userId]);

  // Iter. 406.fr.73 — The old top-fixed UrgencyBanner has been retired in
  // favour of the inline `PillarAlertBubble` rendered in the Dashboard,
  // positioned between the pilot-profile pill and the mission tracker (per
  // Yuri's spec). This manager now only owns the Perfect-Day dopamine
  // modal. We keep the discipline polling for the future in case another
  // in-app trigger needs it.

  if (!userId) return null;

  return (
    <AnimatePresence>
      {showPerfectModal && (
        <PerfectDayModal
          fr={fr}
          onClose={() => setShowPerfectModal(false)}
        />
      )}
    </AnimatePresence>
  );
};

// ────────────────────────────────────────────────────────────────────
// PERFECT DAY MODAL — full-screen dopamine celebration (gold/fuchsia).
// ────────────────────────────────────────────────────────────────────
const PerfectDayModal = ({ fr, onClose }) => {
  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
      // Iter. 406.fr.78 — Bumped above Radix Dialog (z-[100]) + explicit
      // pointer-events-auto because Radix applies `pointer-events: none` on
      // body children while a Dialog is open, which was making the X and
      // "ALL IN ALL TIME" buttons unresponsive (user had to hit Android
      // back to dismiss, which also closed the underlying meal modal).
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      style={{ pointerEvents: 'auto' }}
      data-testid="perfect-day-modal"
      onClick={onClose}
    >
      {/* Confetti dots */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {Array.from({ length: 24 }).map((_, i) => (
          <motion.span
            key={i}
            className="absolute w-2 h-2 rounded-full"
            style={{
              left: `${(i * 37) % 100}%`,
              top: `${(i * 53) % 100}%`,
              background: i % 3 === 0 ? '#f59e0b' : i % 3 === 1 ? '#e879f9' : '#facc15',
              boxShadow: '0 0 8px currentColor',
            }}
            initial={{ y: -40, opacity: 0, scale: 0.4 }}
            animate={{ y: 20, opacity: [0, 1, 0.6, 0], scale: [0.4, 1, 0.9, 0.6] }}
            transition={{ duration: 3, delay: (i % 8) * 0.12, repeat: Infinity, repeatDelay: 1.2 }}
          />
        ))}
      </div>

      <motion.div
        initial={{ scale: 0.85, y: 40, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.85, y: 40, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 260, damping: 22 }}
        className="relative w-full max-w-md rounded-2xl border border-amber-400/50 bg-gradient-to-br from-amber-950/95 via-fuchsia-950/85 to-amber-950/95 shadow-[0_0_60px_rgba(251,191,36,0.5)] p-6 text-center overflow-hidden"
        style={{ pointerEvents: 'auto' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="perfect-day-modal-card"
      >
        {/* Ambient glow */}
        <div className="absolute -top-24 -right-24 w-56 h-56 rounded-full bg-fuchsia-500/25 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 w-56 h-56 rounded-full bg-amber-400/30 blur-3xl pointer-events-none" />

        {/* Trophy pulse */}
        <motion.div
          className="mx-auto mb-4 w-20 h-20 rounded-full bg-gradient-to-br from-amber-400 to-fuchsia-500 flex items-center justify-center shadow-[0_0_40px_rgba(251,191,36,0.7)]"
          animate={{ scale: [1, 1.08, 1], rotate: [0, -6, 6, 0] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
        >
          <Trophy className="w-10 h-10 text-black" strokeWidth={2.5} />
        </motion.div>

        <p className="text-[10px] uppercase tracking-[6px] text-amber-300 font-bold mb-2 flex items-center justify-center gap-1.5">
          <Sparkles className="w-3 h-3" />
          {fr ? 'Score de Discipline Parfait' : 'Perfect Discipline Score'}
          <Sparkles className="w-3 h-3" />
        </p>

        <h2
          className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-amber-300 via-fuchsia-300 to-amber-300 mb-3"
          data-testid="perfect-day-modal-title"
        >
          6/6 🏆
        </h2>

        <p className="text-sm text-foreground/90 max-w-xs mx-auto mb-6 leading-relaxed">
          {fr
            ? "Tu viens de mettre le 1% Extra Miles en pratique aujourd'hui. Lâche rien !"
            : "You just put the 1% Extra Miles into practice today. Never let up!"}
        </p>

        <div className="flex flex-col sm:flex-row gap-2 items-center justify-center">
          <button
            type="button"
            onClick={onClose}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-amber-400 to-fuchsia-500 hover:from-amber-300 hover:to-fuchsia-400 text-black font-black text-xs uppercase tracking-widest px-6 py-3 transition-all shadow-[0_0_25px_rgba(232,121,249,0.5)]"
            data-testid="perfect-day-modal-close"
          >
            <Trophy className="w-4 h-4" />
            {fr ? 'ALL IN ALL TIME' : 'ALL IN ALL TIME'}
          </button>
        </div>

        {/* Corner close */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors"
          aria-label={fr ? 'Fermer' : 'Close'}
          data-testid="perfect-day-modal-corner-close"
        >
          <X className="w-4 h-4" />
        </button>
      </motion.div>
    </motion.div>,
    document.body
  );
};

export default InAppNotificationManager;
